/**
 * rotationLookup — who is teaching a given lesson, according to the released
 * rotation.
 *
 * WHY THIS EXISTS. `timeSlots` (what/when/who-with) and `scheduleAssignments`
 * (which teacher covers which block) were deliberately decoupled — CalendarTab
 * says so in its own header: "timeSlots stays decoupled from the rotation
 * engine". That decoupling is correct for WRITES: editing a lesson must never
 * silently repoint a rotation, and generating a rotation must never rewrite a
 * family's lesson. But it left the calendar unable to answer "who is teaching
 * this?", so its only teacher field was `instructor`, a free-text string that
 * defaults to '' and that nothing populates. This module is the READ-side
 * bridge, and it writes nothing.
 *
 * THE KEY, verified against prod 2026-08-30. `functions/lessonMaterializer.js`
 * emits one sourceEvent per lesson occurrence with
 *
 *     externalId = `timeslot_${slot.id}_${originDate}`
 *
 * and the generator stores exactly that string on each assignment as
 * `sourceEventId` (it is also the sourceEvents DOC id for timeSlots-derived
 * events). So a calendar occurrence — which carries `_slotId` and
 * `_originDate` — can rebuild the key deterministically. No fuzzy matching, no
 * schema change, no new collection. All 126 assignments on the released
 * September run use this shape, so the per-lesson join covers the whole run
 * rather than a lucky subset.
 *
 * ORIGIN DATE, NOT DISPLAYED DATE. The materializer keys on `originDate`, so a
 * lesson moved across days by an `exceptions` patch keeps the key of the day it
 * recurs from. Using the rendered date would silently miss exactly the lessons
 * someone has already had to fix by hand.
 *
 * THE DAY FALLBACK RETURNS null WHEN A DAY IS SPLIT, and that is the important
 * part. `dayLocationKey` answers "who has this location today", which is right
 * only while one teacher has the whole block. A timeslot rule can hand a single
 * slot to someone else, and then the day-level answer is ambiguous — so a mixed
 * day resolves to null and the caller shows nothing. Naming a teacher who is not
 * actually taking the lesson is worse than naming none: the first is a confident
 * wrong answer a human will act on, the second is visibly missing.
 */

// The canonical client-side normalizer already lives with the scheduling model
// ('Lakeville' -> 'LAKEVILLE'), and dayLocationKey is built from it. Importing
// it rather than re-deriving is the point: two normalizers that disagree by one
// casing rule would drop every fallback lookup silently.
import { normalizeLocation } from '../components/Portal/Scheduling/schedulingModel';

/** Rebuild the materializer's externalId for one lesson occurrence. */
export function sourceEventKey(slotId, originDate) {
	if (!slotId || !originDate) return null;
	return `timeslot_${slotId}_${originDate}`;
}

/** "YYYY-MM-DD::LOCATION", matching the generator's dayLocationKey. */
export function dayLocationKey(date, location) {
	const loc = normalizeLocation(location);
	if (!date || !loc) return null;
	return `${date}::${loc}`;
}

/**
 * Index a released run's assignments for lookup.
 *
 * @param {Array} assignments rows from teacherViewData.fetchLatestSchedule —
 *   each { sourceEventId, teacherId, date, location }.
 * @returns {{byEvent: Map<string,string>, byDayLocation: Map<string,string|null>}}
 *   byDayLocation maps to null where the day carries more than one teacher.
 */
export function buildRotationLookup(assignments) {
	const byEvent = new Map();
	const byDayLocation = new Map();
	if (!Array.isArray(assignments)) return { byEvent, byDayLocation };

	// Two teachers seen on one day+location => ambiguous, so store null. A Set
	// per key would allocate for every clean day; tracking the first id and
	// flipping to null on disagreement costs nothing.
	for (const a of assignments) {
		if (!a || !a.teacherId) continue;
		if (a.sourceEventId) byEvent.set(a.sourceEventId, a.teacherId);

		const key = a.date && a.location ? `${a.date}::${normalizeLocation(a.location) || a.location}` : null;
		if (!key) continue;
		if (!byDayLocation.has(key)) byDayLocation.set(key, a.teacherId);
		else if (byDayLocation.get(key) !== a.teacherId) byDayLocation.set(key, null);
	}
	return { byEvent, byDayLocation };
}

/**
 * Resolve the teacher for one calendar occurrence.
 *
 * Per-lesson first (honours a timeslot-rule override), then the day+location
 * fallback for occurrences the run never enumerated — a make-up added after the
 * run was generated, say. Returns null when nothing answers, which the caller
 * must render as "no teacher shown" rather than as "unassigned": the rotation
 * not knowing is different from the rotation saying nobody.
 *
 * `dateStr` is the RENDERED day as "YYYY-MM-DD", passed in rather than derived
 * from occ._date. Deriving it would mean a Date->string conversion inside a
 * service, which is precisely where this codebase's UTC-shift trap lives; the
 * caller already holds the correct local day string for the column it is
 * drawing. Note it is intentionally the rendered day, NOT _originDate: the
 * per-lesson key above owns recurrence identity, while the fallback asks "who
 * is at this location on the day this card appears".
 */
export function teacherIdForOccurrence(lookup, occ, dateStr) {
	if (!lookup || !occ) return null;
	const perLesson = sourceEventKey(occ._slotId, occ._originDate);
	if (perLesson && lookup.byEvent.has(perLesson)) return lookup.byEvent.get(perLesson);

	// A MAKEUP is its own timeSlots doc, so the run never enumerated it — but it
	// carries the slot + date it was freed from, and the rotation's row for THAT
	// lesson names who holds that exact time. This is what resolves a makeup on
	// a split day, where the day fallback below rightly answers null. Same order
	// as the pay stamp (functions/lessonTeacher.js), so a screen and a pay
	// record agree about who taught.
	if (occ.isMakeup) {
		const fromSource = sourceEventKey(occ.sourceSlotId, occ.sourceDate);
		if (fromSource && lookup.byEvent.has(fromSource)) return lookup.byEvent.get(fromSource);
	}

	const dayKey = dayLocationKey(dateStr, occ.location);
	if (dayKey && lookup.byDayLocation.has(dayKey)) return lookup.byDayLocation.get(dayKey) || null;
	return null;
}

/**
 * The teacher STORED on a session record, when it has one (card
 * dQzkKYcUNzuvhO0DEDES, 2026-09-23). The nightly stamp writes it from the
 * rotation and an admin can correct it on Lessons Taught; once present it is
 * the pay fact, so a screen that shows "who teaches this" prefers it over a
 * fresh rotation join — otherwise an admin's correction would be invisible
 * everywhere except the page it was made on. 'unresolved' is not a teacher.
 */
export function teacherIdFromRecord(record) {
	if (!record || !record.teacherId || !record.teacherSource || record.teacherSource === 'unresolved') return null;
	return record.teacherId;
}
